export interface LanguageDetectionResult {
  language: string;
  source: "browser" | "system" | "localStorage" | "fallback";
  confidence: number;
}

export const normalizeLanguageCode = (lang: string): string => {
  if (!lang) return "";

  const normalized = lang.toLowerCase().split("-")[0].split("_")[0];
  return normalized;
};

export const getBrowserLanguages = (): string[] => {
  if (typeof window === "undefined") return [];

  const languages: string[] = [];

  if (navigator.languages && navigator.languages.length > 0) {
    languages.push(...navigator.languages);
  }

  if (navigator.language) {
    languages.push(navigator.language);
  }

  if ((navigator as any).userLanguage) {
    languages.push((navigator as any).userLanguage);
  }

  if ((navigator as any).browserLanguage) {
    languages.push((navigator as any).browserLanguage);
  }

  if ((navigator as any).systemLanguage) {
    languages.push((navigator as any).systemLanguage);
  }

  return Array.from(new Set(languages.map(normalizeLanguageCode))).filter(
    Boolean
  );
};

export const getSystemLanguage = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;

    if (locale) {
      return normalizeLanguageCode(locale);
    }

    if (timeZone) {
      const regionMap: Record<string, string> = {
        "Europe/Berlin": "de",
        "Europe/Madrid": "es",
        "Europe/Rome": "it",
        "America/New_York": "en",
        "America/Los_Angeles": "en",
      };

      return regionMap[timeZone] || null;
    }
  } catch (error) {
    console.warn("Failed to detect system language:", error);
  }

  return null;
};

export const getStoredLanguage = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem("i18nextLng");
    if (stored && stored !== "undefined") {
      return normalizeLanguageCode(stored);
    }
  } catch (error) {
    console.warn("Failed to get stored language:", error);
  }

  return null;
};

export const detectBestLanguage = (
  supportedLanguages: string[]
): LanguageDetectionResult => {
  const storedLang = getStoredLanguage();
  if (storedLang && !supportedLanguages.includes(storedLang)) {
    return {
      language: storedLang,
      source: "localStorage",
      confidence: 1.0,
    };
  }

  const browserLanguages = getBrowserLanguages();
  for (const lang of browserLanguages) {
    if (supportedLanguages.includes(lang)) {
      return {
        language: lang,
        source: "browser",
        confidence: 0.9,
      };
    }
  }

  const systemLang = getSystemLanguage();
  if (systemLang && supportedLanguages.includes(systemLang)) {
    return {
      language: systemLang,
      source: "system",
      confidence: 0.7,
    };
  }

  return {
    language: supportedLanguages[0] || "en",
    source: "fallback",
    confidence: 0.1,
  };
};

export const isRTLLanguage = (language: string): boolean => {
  const rtlLanguages = [
    "ar", // Arabic
    "he", // Hebrew
    "fa", // Persian/Farsi
    "ur", // Urdu
    "ku", // Kurdish
    "dv", // Dhivehi/Maldivian
    "sd", // Sindhi
    "ckb", // Central Kurdish (Sorani)
    "arc", // Aramaic
    "ji", // Yiddish
    "syr", // Syriac
    "azb", // South Azerbaijani
    "glk", // Gilaki
    "lrc", // Northern Luri
    "mzn", // Mazanderani
    "pnb", // Western Punjabi
    "ps", // Pashto
    "ckb", // Sorani Kurdish
    "rhg", // Rohingya
  ];
  return rtlLanguages.includes(normalizeLanguageCode(language));
};

export const getTextDirection = (language: string): "ltr" | "rtl" => {
  return isRTLLanguage(language) ? "rtl" : "ltr";
};

export const getRTLLanguages = (): string[] => {
  return [
    "ar", // Arabic
    "he", // Hebrew
    "fa", // Persian/Farsi
    "ur", // Urdu
    "ku", // Kurdish
    "dv", // Dhivehi/Maldivian
    "sd", // Sindhi
    "ckb", // Central Kurdish (Sorani)
    "arc", // Aramaic
    "ji", // Yiddish
    "syr", // Syriac
  ];
};

export const getLanguageDisplayName = (
  language: string,
  displayLanguage?: string
): string => {
  try {
    const displayNames = new Intl.DisplayNames([displayLanguage || language], {
      type: "language",
    });
    return displayNames.of(language) || language;
  } catch (error) {
    const languageNames: Record<string, string> = {
      en: "English",
      de: "Deutsch",
      uk: "Українська",
      pl: "Polski",
      es: "Español",
      it: "Italiano",
      pt: "Português",
      ru: "Русский",
      zh: "中文",
      ja: "日本語",
      ko: "한국어",
      ar: "العربية",
    };

    return languageNames[normalizeLanguageCode(language)] || language;
  }
};

if (typeof window !== "undefined") {
  (window as any).detectBestLanguage = detectBestLanguage;
  (window as any).getBrowserLanguages = getBrowserLanguages;
  (window as any).getSystemLanguage = getSystemLanguage;
}
